import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  TextInput,
  ActivityIndicator,
  Platform,
} from 'react-native';
import * as WebBrowser from 'expo-web-browser';
import AnimatedPressable from '@/components/AnimatedPressable';
import {
  Star,
  XCircle,
  MessageSquare,
  UserPlus,
  FileText,
  Eye,
  Calendar,
  Clock,
  AlertTriangle,
  Shield,
  TrendingUp,
  Tag,
  Archive,
  ArrowRight,
} from 'lucide-react-native';
import Animated, { FadeIn, FadeInDown } from 'react-native-reanimated';
import { useThemeColors } from '@/lib/useThemeColors';
import { CompanyAvatar } from '@/components/CompanyAvatar';
import { getScoreColor } from '@/lib/colors';
import { noOutlineStyle } from '@/lib/platform';
import { SubmissionScoreDetail } from './SubmissionScoreDetail';
import { StatusBadge } from '@/components/ui';
import { SectionDivider } from '@/components/SectionDivider';
import { AppModal, ModalAction } from '@/components/modal';
import { useAlert } from '@/components/AlertDialog';
import { getSignedUrl } from '@/lib/storage/service';
import { handleError } from '@/lib/errors';
import { mode } from '@/lib/config/app-mode';
import type { MatchedClosedCompany, Submission, SubmissionStatus } from '@/lib/supabase/submissions';
import {
  heldDeckPrompt,
  heldForClosedCompany,
  submissionTags,
  submissionLogoUrl,
  submissionLogoIsKnockout,
} from '@/lib/supabase/submissions';

// ============================================================================
// Helpers
// ============================================================================

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatRelative(dateStr: string): string {
  const now = Date.now();
  const date = new Date(dateStr).getTime();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffMin < 2) return 'just now';
  if (diffMin < 60) return `${diffMin} minutes ago`;
  if (diffHours < 24) return `${diffHours} hours ago`;
  if (diffDays === 1) return 'yesterday';
  return `${diffDays} days ago`;
}

function formatFileSize(bytes: number): string {
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB`;
  if (bytes >= 1_000) return `${(bytes / 1_000).toFixed(0)} KB`;
  return `${bytes} B`;
}

function formatAsk(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value}`;
}

function getStatusConfig(status: SubmissionStatus, colors: ReturnType<typeof useThemeColors>) {
  const fallback = { label: 'Unknown', color: colors.text.tertiary, bg: colors.light[300] };
  const map: Record<SubmissionStatus, { label: string; color: string; bg: string }> = {
    pending: { label: 'Pending', color: colors.text.tertiary, bg: colors.light[300] },
    scoring: { label: 'Scoring', color: colors.warning, bg: colors.amber.light },
    scored: { label: 'Scored', color: colors.primary[600], bg: colors.primary[50] },
    shortlisted: { label: 'Shortlisted', color: colors.success, bg: colors.traffic.greenLight },
    archived: { label: 'Archived', color: colors.text.tertiary, bg: colors.light[300] },
    rejected: { label: 'Rejected', color: colors.error, bg: colors.red.light },
    unscorable: { label: 'Not Scored', color: colors.warning, bg: colors.amber.light },
  };
  // Defensive: submissions.status is a CHECK-constrained text column that can
  // drift ahead of this map; never let an unmapped status crash the modal.
  return (map as Record<string, typeof fallback>)[status] ?? fallback;
}

/**
 * Determine which actions are available for a given status.
 */
function getAvailableActions(status: SubmissionStatus) {
  switch (status) {
    case 'scored':
      return { archive: true, reject: true, requestInfo: true, shortlist: true, inviteClaim: false };
    case 'shortlisted':
      return { archive: true, reject: true, requestInfo: true, shortlist: false, inviteClaim: true };
    case 'archived':
      return { archive: false, reject: true, requestInfo: false, shortlist: true, inviteClaim: false };
    case 'rejected':
      return { archive: true, reject: false, requestInfo: false, shortlist: true, inviteClaim: false };
    case 'unscorable':
      // No reliable score, but the investor can still triage: request more info,
      // archive, reject, or manually shortlist on their own read of the deck.
      return { archive: true, reject: true, requestInfo: true, shortlist: true, inviteClaim: false };
    case 'pending':
    case 'scoring':
    default:
      return { archive: true, reject: false, requestInfo: false, shortlist: false, inviteClaim: false };
  }
}

// ============================================================================
// Props
// ============================================================================

interface SubmissionDetailModalProps {
  visible: boolean;
  onClose: () => void;
  submission: Submission | null;
  /** Resolves false when the modal must stay open (e.g. the shortlist needs a closed-company decision). */
  onShortlist: (id: string) => Promise<boolean | void>;
  onReject: (id: string) => Promise<void>;
  onArchive: (id: string) => Promise<void>;
  /**
   * 119-07: the closed company a shortlist just reported this deck matches
   * (409 matched_closed_company). A deck HELD for that decision at submission
   * time is detected from the row itself (heldForClosedCompany).
   */
  closedMatch?: MatchedClosedCompany | null;
  /**
   * "Different, continue". `held`: the deck was held at submission →
   * submission-action confirm-different-company. `shortlist`: the shortlist
   * found the match → shortlist again with confirm_no_conflict.
   */
  onConfirmDifferentCompany?: (id: string, via: 'held' | 'shortlist') => Promise<boolean | void>;
  onRequestInfo: (id: string, message?: string) => void;
  onInviteToClaim: (id: string) => void;
}

// ============================================================================
// Component
// ============================================================================

export function SubmissionDetailModal({
  visible,
  onClose,
  submission,
  onShortlist,
  onReject,
  onArchive,
  onRequestInfo,
  onInviteToClaim,
  closedMatch = null,
  onConfirmDifferentCompany,
}: SubmissionDetailModalProps) {
  const colors = useThemeColors();
  const { error } = useAlert();
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestMessage, setRequestMessage] = useState('');
  const [showInviteConfirm, setShowInviteConfirm] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [viewDeckLoading, setViewDeckLoading] = useState(false);

  const handleClose = useCallback(() => {
    setShowRequestForm(false);
    setRequestMessage('');
    setShowInviteConfirm(false);
    setActionLoading(null);
    onClose();
  }, [onClose]);

  if (!submission) return null;

  // A closed-company decision pending (119-03 / 119-07): it comes first, and
  // the ordinary triage actions wait for it.
  const held = heldForClosedCompany(submission);
  const decision: MatchedClosedCompany | null = held ?? closedMatch;
  const decisionVia: 'held' | 'shortlist' = held ? 'held' : 'shortlist';

  const statusCfg = held
    ? { label: 'Matches a closed company', color: colors.warning, bg: colors.amber.light }
    : getStatusConfig(submission.status, colors);
  // Derived tags (Claimed/Invited) shown alongside the scoring status.
  const tags = submissionTags(submission);
  const actions = getAvailableActions(submission.status);

  const expiresAt = submission.expires_at;
  const daysLeft = expiresAt
    ? Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 86_400_000)
    : null;

  const handleAction = async (type: string, fn: () => Promise<boolean | void>) => {
    setActionLoading(type);
    try {
      const done = await fn();
      // close on success (the list refetches via the mutation's query
      // invalidation) — unless the handler says the decision is still open
      if (done !== false) handleClose();
    } catch {
      // errors are surfaced inside the handler (handleArchive/handleReject/handleShortlist call handleError)
    } finally {
      setActionLoading(null);
    }
  };

  const handleSendRequest = () => {
    setActionLoading('requestInfo');
    onRequestInfo(submission.id, requestMessage || undefined);
    setShowRequestForm(false);
    setRequestMessage('');
    setTimeout(() => setActionLoading(null), 300);
  };

  const handleConfirmInvite = () => {
    setActionLoading('inviteClaim');
    onInviteToClaim(submission.id);
    setShowInviteConfirm(false);
    setTimeout(() => setActionLoading(null), 300);
  };

  const handleViewDeck = async () => {
    if (!submission.deck_storage_path) return;
    setViewDeckLoading(true);
    try {
      let url: string;
      if (mode.isMock) {
        // In mock mode, open a sample PDF to demonstrate the flow
        url = 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf';
      } else {
        const result = await getSignedUrl('submission-decks', submission.deck_storage_path, 3600);
        if (result.error || !result.url) {
          error('Error', 'Could not load the pitch deck. Please try again.');
          return;
        }
        url = result.url;
      }
      if (Platform.OS === 'web') {
        window.open(url, '_blank');
      } else {
        await WebBrowser.openBrowserAsync(url);
      }
    } catch (err) {
      const { message, isCancelled } = handleError(err, { context: 'SubmissionDetailModal.handleViewDeck' });
      if (!isCancelled) error('Error', message);
    } finally {
      setViewDeckLoading(false);
    }
  };

  const footer = decision ? (
    <AppModal.Actions>
      <ModalAction
        label="Dismiss"
        variant="neutral"
        icon={<Archive size={18} color={colors.text.secondary} />}
        onPress={() => handleAction('dismiss', () => onArchive(submission.id))}
        disabled={actionLoading !== null}
        accessibilityLabel={`Dismiss ${submission.company_name}; nothing is scored`}
      />
      {onConfirmDifferentCompany && (
        <ModalAction
          label="Different, continue"
          variant="primary"
          icon={<ArrowRight size={18} color={colors.text.inverse} />}
          onPress={() =>
            handleAction('confirmDifferent', () => onConfirmDifferentCompany(submission.id, decisionVia))
          }
          disabled={actionLoading !== null}
          accessibilityLabel={`${submission.company_name} is a different company; continue`}
        />
      )}
    </AppModal.Actions>
  ) : (
    <AppModal.Actions>
      {/* Archive removed — Reject already archives + tags; a separate Archive
          action was redundant. */}

      {actions.reject && (
        <ModalAction
          label="Reject"
          variant="destructive"
          icon={<XCircle size={18} color={colors.error} />}
          onPress={() => handleAction('reject', () => onReject(submission.id))}
          disabled={actionLoading === 'reject'}
          accessibilityLabel={`Reject ${submission.company_name}`}
        />
      )}

      {actions.requestInfo && (
        <ModalAction
          label="Request Info"
          variant="neutral"
          icon={
            <MessageSquare
              size={18}
              color={showRequestForm ? colors.primary[600] : colors.text.secondary}
            />
          }
          onPress={() => setShowRequestForm(!showRequestForm)}
          accessibilityLabel="Request more information"
        />
      )}

      {actions.shortlist && (
        <ModalAction
          label="Shortlist"
          variant="success"
          icon={<Star size={18} color={colors.success} />}
          onPress={() => handleAction('shortlist', () => onShortlist(submission.id))}
          disabled={actionLoading === 'shortlist'}
          accessibilityLabel={`Shortlist ${submission.company_name}`}
        />
      )}

      {actions.inviteClaim && (
        <ModalAction
          label="Invite to Claim"
          variant="primary"
          icon={<UserPlus size={18} color={colors.text.inverse} />}
          onPress={() => setShowInviteConfirm(!showInviteConfirm)}
          accessibilityLabel="Invite startup to claim profile"
        />
      )}
    </AppModal.Actions>
  );

  return (
    <AppModal
      visible={visible}
      onClose={handleClose}
      scrollToEndSignal={showRequestForm || showInviteConfirm}
      title={submission.company_name}
      headerRight={
        <View className="flex-row items-center gap-1.5 flex-wrap justify-end">
          <StatusBadge
            label={statusCfg.label}
            color={statusCfg.color}
            backgroundColor={statusCfg.bg}
            size="md"
          />
          {tags.map((t) => (
            <StatusBadge
              key={t.key}
              label={t.label}
              size="md"
              outlined
              icon={Tag}
              color={colors.primary[600]}
              backgroundColor={colors.primary[50]}
            />
          ))}
        </View>
      }
      footer={footer}
    >
      {/* Sender identity block */}
      <View className="flex-row items-center gap-3">
        <CompanyAvatar
          name={submission.company_name}
          logoUrl={submissionLogoUrl(submission)}
          isKnockout={submissionLogoIsKnockout(submission)}
          size={48}
          radius={12}
        />
        <View className="flex-1">
          <Text style={{ color: colors.text.tertiary }} className="text-sm" numberOfLines={2}>
            {submission.contact_email} {'·'} Submitted {formatDate(submission.created_at)}
          </Text>
        </View>
      </View>

      {submission.submitter_note && submission.submitter_note.trim().length > 0 && (
        <View
          style={{
            marginTop: 10,
            paddingVertical: 8,
            paddingHorizontal: 12,
            borderLeftWidth: 3,
            borderLeftColor: colors.primary[500],
            backgroundColor: colors.surface.secondary,
            borderRadius: 6,
          }}
        >
          <Text
            numberOfLines={1}
            style={{ color: colors.text.tertiary, fontSize: 11, fontWeight: '600', letterSpacing: 0.4, textTransform: 'uppercase', marginBottom: 2 }}
          >
            Note from submitter
          </Text>
          <Text numberOfLines={2} style={{ color: colors.text.secondary, fontSize: 13, lineHeight: 19, fontStyle: 'italic' }}>
            "{submission.submitter_note.trim()}"
          </Text>
        </View>
      )}

      {/* Scoring in progress */}
      {submission.status === 'scoring' && (
        <Animated.View entering={FadeIn.delay(100).duration(400)} className="pt-5">
          <View
            className="rounded-2xl px-5 py-4 flex-row items-center"
            style={{ backgroundColor: colors.surface.secondary, borderWidth: 1, borderColor: colors.border.light }}
          >
            <ActivityIndicator color={colors.primary[600]} />
            <Text style={{ color: colors.text.secondary }} className="text-sm ml-3 flex-1">
              Scoring this deck&hellip; this usually takes under a minute.
            </Text>
          </View>
        </Animated.View>
      )}

      {/* Matches a company that closed on Inverio — the investor decides */}
      {decision && (
        <Animated.View entering={FadeIn.delay(100).duration(400)} className="pt-5">
          <View
            testID="closed-company-decision"
            className="rounded-2xl px-5 py-4 flex-row items-start overflow-hidden"
            style={{ backgroundColor: colors.amber.light, borderWidth: 1, borderColor: colors.amber.muted }}
          >
            <View className="shrink-0" style={{ paddingTop: 2 }}>
              <AlertTriangle size={20} color={colors.warning} />
            </View>
            <View className="flex-1 ml-3">
              <Text numberOfLines={2} style={{ color: colors.text.primary }} className="text-base font-semibold mb-1">
                Matches a closed company
              </Text>
              <Text numberOfLines={5} style={{ color: colors.text.secondary }} className="text-sm leading-5">
                {heldDeckPrompt(decision)}
              </Text>
              <Text numberOfLines={4} style={{ color: colors.text.tertiary }} className="text-xs leading-4 mt-2">
                Continue to score it as a new, separate company. Dismiss archives it; nothing is scored.
              </Text>
            </View>
          </View>
        </Animated.View>
      )}

      {/* Genuinely unscorable — honest state, never a fabricated number */}
      {submission.status === 'unscorable' && !decision && (
        <Animated.View entering={FadeIn.delay(100).duration(400)} className="pt-5">
          <View
            className="rounded-2xl px-5 py-4 flex-row items-start"
            style={{ backgroundColor: colors.surface.secondary, borderWidth: 1, borderColor: colors.border.light }}
          >
            <AlertTriangle size={20} color={colors.warning} />
            <View className="flex-1 ml-3">
              <Text style={{ color: colors.text.primary }} className="text-base font-semibold mb-1">
                Couldn&apos;t score automatically
              </Text>
              <Text style={{ color: colors.text.secondary }} className="text-sm leading-5">
                We couldn&apos;t read enough from this deck to produce a reliable Inverio Readiness Score —
                the PDF may be unreadable or missing key information. You can still review the deck and
                request more details from the founder.
              </Text>
            </View>
          </View>
        </Animated.View>
      )}

      {/* Rich IRS breakdown — fetched from the canonical score_event */}
      {submission.status !== 'unscorable' &&
        submission.ai_score !== null &&
        submission.startup_profile_id && (
          <Animated.View entering={FadeIn.delay(100).duration(400)} className="pt-5">
            <SubmissionScoreDetail
              startupProfileId={submission.startup_profile_id}
              fallbackScore={submission.ai_score}
              confidence={submission.ai_confidence}
            />
          </Animated.View>
        )}

      {/* Legacy fallback: scored before the link existed (no profile to resolve) */}
      {submission.status !== 'unscorable' &&
        submission.ai_score !== null &&
        !submission.startup_profile_id && (
          <Animated.View entering={FadeIn.delay(100).duration(400)} className="pt-5">
            <View
              className="flex-row rounded-2xl overflow-hidden"
              style={{ backgroundColor: colors.surface.secondary, borderWidth: 1, borderColor: colors.border.light }}
            >
              <View className="flex-1 items-center py-5">
                <Text
                  style={{ color: getScoreColor(submission.ai_score) }}
                  className="text-4xl font-bold"
                  accessibilityLabel={`Overall score ${submission.ai_score} out of 100`}
                >
                  {submission.ai_score}
                </Text>
                <Text style={{ color: colors.text.tertiary }} className="text-xs mt-0.5">/100 Score</Text>
              </View>
              <View style={{ width: 1, backgroundColor: colors.border.light }} />
              <View className="flex-1 items-center py-5">
                <View className="flex-row items-center gap-1">
                  <Shield size={18} color={colors.primary[600]} />
                  <Text style={{ color: colors.primary[600] }} className="text-2xl font-bold">
                    {submission.ai_confidence}%
                  </Text>
                </View>
                <Text style={{ color: colors.text.tertiary }} className="text-xs mt-0.5">Confidence</Text>
              </View>
            </View>
          </Animated.View>
        )}

      {/* Extracted Information */}
      {submission.ai_extracted_data && (
        <Animated.View entering={FadeInDown.delay(300).duration(400)}>
          <SectionDivider label="Company Details" marginTop={24} marginBottom={12} />
          <View
            className="p-4 rounded-2xl gap-3"
            style={{
              backgroundColor: colors.surface.primary,
              borderWidth: 1,
              borderColor: colors.border.light,
            }}
          >
            {[
              { label: 'Sector', value: submission.ai_extracted_data.sector },
              { label: 'Stage', value: submission.ai_extracted_data.stage },
              { label: 'Ask Amount', value: submission.ai_extracted_data.ask_amount ? formatAsk(submission.ai_extracted_data.ask_amount) : null },
              { label: 'Team Size', value: submission.ai_extracted_data.team_size?.toString() },
              { label: 'Contact', value: submission.contact_name ?? submission.contact_email },
            ].filter(item => item.value).map((item) => (
              <View key={item.label} className="flex-row items-center justify-between gap-3">
                <Text style={{ color: colors.text.tertiary }} className="text-sm" numberOfLines={1}>{item.label}</Text>
                <Text style={{ color: colors.text.primary }} className="text-sm font-medium capitalize flex-shrink" numberOfLines={1}>
                  {item.value}
                </Text>
              </View>
            ))}

            {submission.ai_extracted_data.one_liner && (
              <View className="pt-3 border-t" style={{ borderColor: colors.border.light }}>
                <Text style={{ color: colors.text.tertiary }} className="text-xs mb-1" numberOfLines={1}>One-liner</Text>
                <Text style={{ color: colors.text.primary }} className="text-sm italic" numberOfLines={2}>
                  &quot;{submission.ai_extracted_data.one_liner}&quot;
                </Text>
              </View>
            )}
          </View>
        </Animated.View>
      )}

      {/* Deck Info */}
      <Animated.View entering={FadeInDown.delay(350).duration(400)}>
        <SectionDivider label="Pitch Deck" marginTop={24} marginBottom={12} />
        <View
          className="p-4 rounded-2xl flex-row items-center gap-3"
          style={{
            backgroundColor: colors.surface.primary,
            borderWidth: 1,
            borderColor: colors.border.light,
          }}
        >
          <View
            className="w-12 h-12 rounded-xl items-center justify-center"
            style={{ backgroundColor: colors.primary[50] }}
          >
            <FileText size={24} color={colors.primary[600]} />
          </View>
          <View className="flex-1">
            <Text style={{ color: colors.text.primary }} className="font-medium" numberOfLines={1}>
              {submission.company_name} Pitch Deck
            </Text>
            <Text style={{ color: colors.text.tertiary }} className="text-sm" numberOfLines={1}>
              PDF {'·'} {formatFileSize(submission.deck_size_bytes)}
            </Text>
          </View>
          {submission.deck_storage_path && (
            <AnimatedPressable
              onPress={handleViewDeck}
              disabled={viewDeckLoading}
              accessibilityRole="button"
              accessibilityLabel="View pitch deck"
              className="w-10 h-10 rounded-xl items-center justify-center"
              style={{ backgroundColor: colors.primary[50] }}
            >
              {viewDeckLoading ? (
                <ActivityIndicator size="small" color={colors.primary[600]} />
              ) : (
                <Eye size={20} color={colors.primary[600]} />
              )}
            </AnimatedPressable>
          )}
        </View>
      </Animated.View>

      {/* Timeline */}
      <Animated.View entering={FadeInDown.delay(400).duration(400)}>
        <SectionDivider label="Timeline" marginTop={24} marginBottom={12} />
        <View
          className="p-4 rounded-2xl gap-3"
          style={{
            backgroundColor: colors.surface.primary,
            borderWidth: 1,
            borderColor: colors.border.light,
          }}
        >
          <View className="flex-row items-center gap-3">
            <Calendar size={16} color={colors.text.tertiary} />
            <View className="flex-1">
              <Text style={{ color: colors.text.secondary }} className="text-sm" numberOfLines={1}>Submitted</Text>
              <Text style={{ color: colors.text.primary }} className="text-sm font-medium" numberOfLines={1}>
                {formatDate(submission.created_at)} ({formatRelative(submission.created_at)})
              </Text>
            </View>
          </View>

          {submission.scored_at && (
            <View className="flex-row items-center gap-3">
              <TrendingUp size={16} color={colors.primary[600]} />
              <View className="flex-1">
                <Text style={{ color: colors.text.secondary }} className="text-sm" numberOfLines={1}>Scored</Text>
                <Text style={{ color: colors.text.primary }} className="text-sm font-medium" numberOfLines={1}>
                  {formatDate(submission.scored_at)} ({formatRelative(submission.scored_at)})
                </Text>
              </View>
            </View>
          )}

          {submission.expires_at && (
            <View className="flex-row items-center gap-3">
              <Clock
                size={16}
                color={daysLeft !== null && daysLeft <= 7 ? colors.error : colors.text.tertiary}
              />
              <View className="flex-1">
                <Text style={{ color: colors.text.secondary }} className="text-sm" numberOfLines={1}>Expires</Text>
                <Text
                  numberOfLines={1}
                  style={{
                    color: daysLeft !== null && daysLeft <= 7 ? colors.error : colors.text.primary,
                  }}
                  className="text-sm font-medium"
                >
                  {formatDate(submission.expires_at)}
                  {daysLeft !== null && (
                    daysLeft < 0
                      ? ' (Expired)'
                      : ` (${daysLeft} days remaining)`
                  )}
                </Text>
              </View>
            </View>
          )}
        </View>
      </Animated.View>

      {/* Request Info Form (inline) */}
      {showRequestForm && (
        <Animated.View entering={FadeInDown.duration(300)} className="mt-4">
          <View
            className="p-4 rounded-2xl"
            style={{
              backgroundColor: colors.primary[50],
              borderWidth: 1,
              borderColor: colors.primary[200],
            }}
          >
            <Text style={{ color: colors.primary[700] }} className="font-semibold mb-2">
              Request More Information
            </Text>
            <Text style={{ color: colors.primary[600] }} className="text-sm mb-3">
              Send a message to the startup asking for more information.
            </Text>
            <TextInput
              value={requestMessage}
              onChangeText={setRequestMessage}
              placeholder="e.g., Could you share your latest financials?"
              placeholderTextColor={colors.text.tertiary}
              multiline
              numberOfLines={3}
              className="rounded-xl p-3 text-sm mb-3"
              style={{
                ...noOutlineStyle({
                  color: colors.text.primary,
                  backgroundColor: colors.surface.primary,
                  borderWidth: 1,
                  borderColor: colors.border.light,
                  minHeight: 80,
                  textAlignVertical: 'top',
                }),
              }}
            />
            <View className="flex-row justify-end gap-2">
              <AnimatedPressable
                onPress={() => {
                  setShowRequestForm(false);
                  setRequestMessage('');
                }}
                accessibilityRole="button"
                accessibilityLabel="Cancel request"
                className="px-4 py-2.5 rounded-lg"
                style={{ backgroundColor: colors.surface.primary }}
              >
                <Text style={{ color: colors.text.secondary }} className="font-medium text-sm">Cancel</Text>
              </AnimatedPressable>
              <AnimatedPressable
                onPress={handleSendRequest}
                disabled={actionLoading === 'requestInfo'}
                accessibilityRole="button"
                accessibilityLabel="Send information request"
                className="px-4 py-2.5 rounded-lg flex-row items-center gap-2"
                style={{ backgroundColor: colors.primary[600] }}
              >
                {actionLoading === 'requestInfo' ? (
                  <ActivityIndicator size="small" color="white" />
                ) : (
                  <MessageSquare size={16} color="white" />
                )}
                <Text className="text-white font-medium text-sm">Send Request</Text>
              </AnimatedPressable>
            </View>
          </View>
        </Animated.View>
      )}

      {/* Invite to Claim Confirmation */}
      {showInviteConfirm && (
        <Animated.View entering={FadeInDown.duration(300)} className="mt-4">
          <View
            className="p-4 rounded-2xl"
            style={{
              backgroundColor: colors.traffic.greenLight,
              borderWidth: 1,
              borderColor: colors.traffic.greenMuted,
            }}
          >
            <Text style={{ color: colors.success }} className="font-semibold mb-2">
              Invite to Claim Profile
            </Text>
            <Text style={{ color: colors.text.secondary }} className="text-sm mb-3">
              This will send an email to{' '}
              <Text className="font-semibold">{submission.contact_email}</Text>{' '}
              inviting them to claim their Inverio page. Continue?
            </Text>
            <View className="flex-row justify-end gap-2">
              <AnimatedPressable
                onPress={() => setShowInviteConfirm(false)}
                accessibilityRole="button"
                accessibilityLabel="Cancel invite"
                className="px-4 py-2.5 rounded-lg"
                style={{ backgroundColor: colors.surface.primary }}
              >
                <Text style={{ color: colors.text.secondary }} className="font-medium text-sm">Cancel</Text>
              </AnimatedPressable>
              <AnimatedPressable
                onPress={handleConfirmInvite}
                disabled={actionLoading === 'inviteClaim'}
                accessibilityRole="button"
                accessibilityLabel="Confirm invite to claim"
                className="px-4 py-2.5 rounded-lg flex-row items-center gap-2"
                style={{ backgroundColor: colors.success }}
              >
                {actionLoading === 'inviteClaim' ? (
                  <ActivityIndicator size="small" color="white" />
                ) : (
                  <UserPlus size={16} color="white" />
                )}
                <Text className="text-white font-medium text-sm">Send Invite</Text>
              </AnimatedPressable>
            </View>
          </View>
        </Animated.View>
      )}
    </AppModal>
  );
}
